import React, { useEffect, useMemo, useRef, useState } from 'react'
import Modal from '../../../componants/modal/Modal'
import { CONTINENT_ORDER, classifyPortContinent } from '../../../utils/cruiseRegions'
import './cruiseSearchModals.css'

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
// "Special dates" section commented out per request — no field on
// /cruises/search backs it, so selecting one was purely a visual toggle.
// const SPECIAL_DATES = ['Last Minute Cruise Deals', "President's Cruise", 'Weekend Getaways']

const pad2 = (n) => String(n).padStart(2, '0')
const monthKey = (year, monthIndex) => `${year}-${pad2(monthIndex + 1)}`

// Only used by the now-commented-out "Search By Dates" calendar view — "Search By
// Months" is the only date-picking mode left (see stepIndex === 3 below).
// const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
// const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
// const buildCalendarCells = (year, monthIndex) => {
//     const firstDay = new Date(year, monthIndex, 1).getDay()
//     const daysInMonth = new Date(year, monthIndex + 1, 0).getDate()
//     const cells = []
//     for (let i = 0; i < firstDay; i++) cells.push(null)
//     for (let d = 1; d <= daysInMonth; d++) cells.push(d)
//     return cells
// }

const STEPS = [
  { key: 'line', label: 'Cruise Line' },
  { key: 'port', label: 'Port' },
  { key: 'ship', label: 'Ship' },
  { key: 'date', label: 'Date' },
]

const MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const formatMonthLabel = (key) => {
  if (!key) return ''
  const [year, monthNum] = key.split('-').map(Number)
  return `${MONTH_FULL[monthNum - 1]} ${year}`
}

// destination_port_ids / embarkation_ports_ids both take arrays on /cruises/search,
// so build the { label, portIds } shape the search payload/pill summary use from
// however many ports were picked.
const toPortSelection = (pickedPorts) => {
  if (!pickedPorts.length) return null
  const names = pickedPorts.map((port) => port.port_name.replace(/,\s*$/, ''))
  const label = names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`
  return { label, portIds: pickedPorts.map((port) => port.port_id) }
}

// Single guided popup for the whole cruise search: Port -> Cruise Line/Ship -> Date,
// replacing the earlier design of separate per-field popups (Regions tab, hidden
// "more filters" toggle) per explicit feedback that the multi-popup flow didn't work.
const CruiseSearchWizard = ({
  open,
  onClose,
  ports,
  portsLoading,
  lines,
  linesLoading,
  ships,
  shipsLoading,
  initialValues,
  initialStepIndex,
  onDone,
}) => {
  const [stepIndex, setStepIndex] = useState(0)

  // Arrays of picked port objects — destination_port_ids / embarkation_ports_ids both
  // accept multiple ids on /cruises/search, so both fields are multi-select.
  const [destinationPorts, setDestinationPorts] = useState([])
  const [embarkationPorts, setEmbarkationPorts] = useState([])
  const [lineSelection, setLineSelection] = useState([])
  const [shipSelection, setShipSelection] = useState([])
  const [month, setMonth] = useState('')

  const today = useMemo(() => new Date(), [])
  const currentYear = today.getFullYear()
  const currentMonthIndex = today.getMonth()

  const [portField, setPortField] = useState('destination') // 'destination' | 'embarkation'
  const [portSearch, setPortSearch] = useState('')
  // Cruise Line and Ship are now separate wizard steps (not a sub-tab toggle within
  // one step), so there's nothing left to switch between here.
  // const [filterField, setFilterField] = useState('lines') // 'lines' | 'ships'
  const [filterSearch, setFilterSearch] = useState('')
  // "Search By Dates" is removed — Months is the only date-picking mode now.
  const [dateTab, setDateTab] = useState('months')
  // Only the setter is still used (reset on modal open) — the "Special
  // dates" UI that read this value is commented out above.
  const [, setSpecialDate] = useState(null)
  // pivot/selectedDay were only used by the now-commented-out "Search By Dates" calendar.
  // const [pivot, setPivot] = useState({ year: currentYear, monthIndex: currentMonthIndex })
  // const [selectedDay, setSelectedDay] = useState(null)
  const [yearWindowStart, setYearWindowStart] = useState(currentYear)

  // Each step's option list is collapsed behind its search input by default —
  // clicking the input opens it, clicking outside the input+list closes it.
  const [listOpen, setListOpen] = useState(false)
  const listAreaRef = useRef(null)

  useEffect(() => {
    setListOpen(false)
  }, [stepIndex])

  useEffect(() => {
    if (!listOpen) return undefined
    const handleClickOutside = (e) => {
      if (listAreaRef.current && !listAreaRef.current.contains(e.target)) {
        setListOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [listOpen])

  useEffect(() => {
    if (!open) return
    setStepIndex(initialStepIndex || 0)
    setPortField('destination')
    setPortSearch('')
    setFilterSearch('')
    setDateTab('months')
    setSpecialDate(null)

    const initDestIds = initialValues?.destinationSelection?.portIds || []
    const initEmbIds = initialValues?.embarkationSelection?.portIds || []
    setDestinationPorts(ports.filter((port) => initDestIds.includes(port.port_id)))
    setEmbarkationPorts(ports.filter((port) => initEmbIds.includes(port.port_id)))
    setLineSelection(initialValues?.lineSelection || [])
    setShipSelection(initialValues?.shipSelection || [])
    const initialMonth = initialValues?.month || ''
    setMonth(initialMonth)

    if (initialMonth) {
      const [y] = initialMonth.split('-').map(Number)
      setYearWindowStart(y)
    } else {
      setYearWindowStart(currentYear)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // ── Step 1: Port ──────────────────────────────────────────────────────
  const currentActivePorts = portField === 'destination' ? destinationPorts : embarkationPorts

  // Already-picked ports get pulled into their own "Selected" group at the very
  // top of the dropdown (ahead of every continent section), so the current
  // selection stays visible without scrolling to find it again.
  const groupedPorts = useMemo(() => {
    const query = portSearch.trim().toLowerCase()
    // Some entries come back from /cruises/ports with no port_name at all —
    // shown as a blank checkbox row otherwise, so they're dropped up front.
    const named = ports.filter((port) => (port.port_name || '').trim())
    const filtered = query ? named.filter((port) => port.port_name.toLowerCase().includes(query)) : named
    const selectedIds = new Set(currentActivePorts.map((p) => p.port_id))
    const selected = filtered.filter((port) => selectedIds.has(port.port_id))
    const groups = {}
    filtered.forEach((port) => {
      if (selectedIds.has(port.port_id)) return
      const continent = classifyPortContinent(port.port_name)
      if (!groups[continent]) groups[continent] = []
      groups[continent].push(port)
    })
    const continentGroups = CONTINENT_ORDER.filter((continent) => groups[continent]?.length).map((continent) => ({
      continent,
      ports: groups[continent],
    }))
    return selected.length ? [{ continent: 'Selected', ports: selected }, ...continentGroups] : continentGroups
  }, [ports, portSearch, currentActivePorts])

  // Only used by the now-commented-out Select All button.
  // const allFilteredPorts = useMemo(() => groupedPorts.flatMap((group) => group.ports), [groupedPorts])

  const activePorts = currentActivePorts
  const setActivePorts = portField === 'destination' ? setDestinationPorts : setEmbarkationPorts

  const togglePort = (port) => {
    setActivePorts((prev) => (
      prev.some((p) => p.port_id === port.port_id)
        ? prev.filter((p) => p.port_id !== port.port_id)
        : [...prev, port]
    ))
    // Clears the typed search text so it doesn't linger in the input
    // alongside the newly-picked chip.
    setPortSearch('')
  }
  const clearActivePorts = () => setActivePorts([])
  // Select All button removed — no selection already means "all ports" at
  // search time (destination_port_ids falls back to ['ALL'] in cruise.jsx).
  // const selectAllPorts = () => setActivePorts(allFilteredPorts)

  // ── Step 2: Cruise Line & Ship ───────────────────────────────────────
  const filteredLines = useMemo(() => {
    const query = filterSearch.trim().toLowerCase()
    // Some entries come back from /cruises/lines with no cruise_name at all —
    // shown as a blank checkbox row otherwise, so they're dropped up front.
    const named = lines.filter((line) => (line.cruise_name || '').trim())
    if (!query) return named
    return named.filter((line) => line.cruise_name.toLowerCase().includes(query))
  }, [lines, filterSearch])

  // Already-picked lines/ports/ships float to the top of their list, so the
  // current selection stays visible without scrolling to find it again.
  const sortedLines = useMemo(() => (
    [...filteredLines].sort((a, b) => (
      (lineSelection.includes(b.cruise_name) ? 1 : 0) - (lineSelection.includes(a.cruise_name) ? 1 : 0)
    ))
  ), [filteredLines, lineSelection])

  const filteredShips = useMemo(() => {
    const query = filterSearch.trim().toLowerCase()
    // Some entries come back from /cruises/ships with no ship_name at all —
    // shown as a blank checkbox row otherwise, so they're dropped up front.
    const named = ships.filter((ship) => (ship.ship_name || '').trim())
    if (!query) return named
    return named.filter((ship) => ship.ship_name.toLowerCase().includes(query))
  }, [ships, filterSearch])

  const sortedShips = useMemo(() => (
    [...filteredShips].sort((a, b) => (
      (shipSelection.includes(b.ship_id) ? 1 : 0) - (shipSelection.includes(a.ship_id) ? 1 : 0)
    ))
  ), [filteredShips, shipSelection])

  const toggleLine = (name) => {
    setLineSelection((prev) => (prev.includes(name) ? prev.filter((v) => v !== name) : [...prev, name]))
    // Clears the typed search text so it doesn't linger in the input
    // alongside the newly-picked chip.
    setFilterSearch('')
  }
  const toggleShip = (id) => {
    setShipSelection((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
    setFilterSearch('')
  }
  // Select All buttons removed — no selection already means "all lines" /
  // "all ships" at search time (cruise_lines / cruise_ships_ids fall back to
  // ['ALL'] in cruise.jsx).
  // const selectAllLines = () => setLineSelection(filteredLines.map((line) => line.cruise_name))
  const clearAllLines = () => setLineSelection([])
  // const selectAllShips = () => setShipSelection(filteredShips.map((ship) => ship.ship_id))
  const clearAllShips = () => setShipSelection([])

  // ── Step 3: Date ──────────────────────────────────────────────────────
  const isBeforeCurrentMonth = (year, monthIndex) => year * 12 + monthIndex < currentYear * 12 + currentMonthIndex

  // shiftPivot/visibleMonths/handleDayClick were only used by the now-commented-out
  // "Search By Dates" calendar view.
  // const shiftPivot = (delta) => {
  //     setPivot((prev) => {
  //         const total = prev.year * 12 + prev.monthIndex + delta
  //         const nextYear = Math.floor(total / 12)
  //         const nextMonthIndex = ((total % 12) + 12) % 12
  //         if (isBeforeCurrentMonth(nextYear, nextMonthIndex)) return prev
  //         return { year: nextYear, monthIndex: nextMonthIndex }
  //     })
  // }

  // const visibleMonths = [0, 1, 2].map((offset) => {
  //     const total = pivot.year * 12 + pivot.monthIndex + offset
  //     return { year: Math.floor(total / 12), monthIndex: ((total % 12) + 12) % 12 }
  // })
  const visibleYears = [0, 1, 2].map((offset) => yearWindowStart + offset)

  // const handleDayClick = (year, monthIndex, day) => {
  //     if (isBeforeCurrentMonth(year, monthIndex)) return
  //     setSelectedDay({ year, month: monthIndex, day })
  //     setMonth(monthKey(year, monthIndex))
  // }
  const handleMonthClick = (year, monthIndex) => {
    if (isBeforeCurrentMonth(year, monthIndex)) return
    setMonth(monthKey(year, monthIndex))
    setListOpen(false)
  }

  // ── Navigation ────────────────────────────────────────────────────────
  const goNext = () => setStepIndex((i) => Math.min(i + 1, STEPS.length - 1))
  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0))

  // "Next"/"Done" were disabled until the current step had a selection —
  // reverted per request, they're always clickable again like before.
  // const hasSelectionForStep = stepIndex === 0
  //   ? lineSelection.length > 0
  //   : stepIndex === 1
  //     ? activePorts.length > 0
  //     : stepIndex === 2
  //       ? shipSelection.length > 0
  //       : !!month

  // "Done" only commits the picked values back to the main page's search pill —
  // it does not run the actual search. The search only fires when the user
  // clicks "Search Cruise" on the main page itself.
  const handleDone = () => {
    if (!month) {
      setDateTab('months')
      return
    }
    onDone({
      destinationSelection: toPortSelection(destinationPorts),
      embarkationSelection: toPortSelection(embarkationPorts),
      lineSelection,
      shipSelection,
      month,
    })
    onClose()
  }

  // Skip clears whatever's picked on the current step (nothing selected
  // already means "all" at search time — see clearAllLines/clearAllShips/
  // clearActivePorts above) and moves on regardless of the Next/Done guard.
  const skipStep = () => {
    if (stepIndex === 0) clearAllLines()
    else if (stepIndex === 1) clearActivePorts()
    else if (stepIndex === 2) clearAllShips()
    else setMonth('')

    if (stepIndex < STEPS.length - 1) {
      goNext()
      return
    }
    onDone({
      destinationSelection: toPortSelection(destinationPorts),
      embarkationSelection: toPortSelection(embarkationPorts),
      lineSelection,
      shipSelection,
      month: '',
    })
    onClose()
  }

  return (
    <Modal open={open} onClose={onClose} maxWidth={980} className='cruiseSearchModalBox'>
      <div className='cruiseSearchModal'>
        {stepIndex > 0 && (
          <button type='button' className='cruiseSearchModal__backBtn' onClick={goBack} aria-label='Go back'>
            <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
              <path d='M10 2L4 8l6 6' stroke='#1D2A4D' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
          </button>
        )}
        <div className='cruiseSearchModal__title'>Search Cruises</div>

        <div className='cruiseWizard__steps'>
          {STEPS.map((step, index) => (
            <React.Fragment key={step.key}>
              <div
                className={`cruiseWizard__step ${index === stepIndex ? 'active' : ''} ${index < stepIndex ? 'done' : ''}`}
                onClick={() => setStepIndex(index)}
                style={{ cursor: 'pointer' }}
              >
                <span className='cruiseWizard__stepNum'>{index + 1}</span>
                <span className='cruiseWizard__stepLabel'>{step.label}</span>
              </div>
              {index < STEPS.length - 1 && <span className='cruiseWizard__stepDivider' />}
            </React.Fragment>
          ))}
        </div>

        <div className='cruiseSearchModal__body cruiseSearchModal__body--scroll'>
          {stepIndex === 0 && (
            <>
              <div className='cruiseWizard__subTabs'>
                <span className='cruiseSearchModal__tab active'>
                  Cruise Line{lineSelection.length ? ` (${lineSelection.length})` : ''}
                </span>
                <div className='cruiseWizard__bulkActions' style={{ marginTop: 0 }}>
                  {/* Select All removed — picking nothing already searches all cruise lines. */}
                  <span className='cruiseSearchModal__clearBtn' onClick={clearAllLines}>Clear All</span>
                </div>
              </div>

              <div className='cruiseSearchModal__fieldWrap' ref={listOpen ? listAreaRef : null}>
                <div className='cruiseSearchModal__searchInput cruiseSearchModal__chipInput mt-4' onClick={() => setListOpen(true)}>
                  {lineSelection.map((name) => (
                    <span className='cruiseSearchModal__chip' key={name}>
                      {name}
                      <span className='cruiseSearchModal__chipRemove' onClick={(e) => { e.stopPropagation(); toggleLine(name) }}>✕</span>
                    </span>
                  ))}
                  <input
                    type='text'
                    placeholder={lineSelection.length ? '' : 'Search cruise lines'}
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    onFocus={() => setListOpen(true)}
                  />
                </div>

                {listOpen && (
                  <div className='cruiseSearchModal__dropdown'>
                    {linesLoading && <div className='cruiseMonthsPicker__sectionTitle mt-4'>Loading...</div>}
                    {!linesLoading && !filteredLines.length && <div className='cruiseMonthsPicker__sectionTitle mt-4'>No results</div>}
                    {!linesLoading && filteredLines.length > 0 && (
                      <div className='cruiseSearchModal__list'>
                        {sortedLines.map((line) => (
                          <label key={line.cruise_name} className='cruiseSearchModal__listItem'>
                            <span className='cruiseSearchModal__listLabel'>{line.cruise_name}</span>
                            <input
                              type='checkbox'
                              checked={lineSelection.includes(line.cruise_name)}
                              onChange={() => toggleLine(line.cruise_name)}
                            />
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {stepIndex === 1 && (
            <>
              {/* "Leaving From" (embarkation port) tab removed — only destination port
                  selection remains, so there's nothing left to switch between. */}
              <div className='cruiseWizard__subTabs'>
                <span className='cruiseSearchModal__tab active'>
                  Cruising To{destinationPorts.length ? ` (${destinationPorts.length})` : ''}
                </span>
                <div className='cruiseWizard__bulkActions' style={{ marginTop: 0 }}>
                  {/* Select All removed — picking nothing already searches all ports. */}
                  <span className='cruiseSearchModal__clearBtn' onClick={clearActivePorts}>Clear All</span>
                </div>
              </div>

              {/* destination_port_ids / embarkation_ports_ids both accept arrays — pick as many as you like. */}
              <div className='cruiseSearchModal__fieldWrap' ref={listOpen ? listAreaRef : null}>
                <div className='cruiseSearchModal__searchInput cruiseSearchModal__chipInput mt-4' onClick={() => setListOpen(true)}>
                  {activePorts.map((port) => (
                    <span className='cruiseSearchModal__chip' key={port.port_id}>
                      {port.port_name.replace(/,\s*$/, '')}
                      <span className='cruiseSearchModal__chipRemove' onClick={(e) => { e.stopPropagation(); togglePort(port) }}>✕</span>
                    </span>
                  ))}
                  <input
                    type='text'
                    placeholder={activePorts.length ? '' : 'Search a port or city'}
                    value={portSearch}
                    onChange={(e) => setPortSearch(e.target.value)}
                    onFocus={() => setListOpen(true)}
                  />
                </div>

                {listOpen && (
                  <div className='cruiseSearchModal__dropdown'>
                    {portsLoading && <div className='cruiseMonthsPicker__sectionTitle mt-4'>Loading ports...</div>}
                    {!portsLoading && groupedPorts.map(({ continent, ports: continentPorts }) => (
                      <div key={continent}>
                        <div className='cruiseMonthsPicker__sectionTitle mt-4'>{continent}</div>
                        <div className='cruiseSearchModal__list'>
                          {continentPorts.map((port) => (
                            <label key={port.port_id} className='cruiseSearchModal__listItem'>
                              <span className='cruiseSearchModal__listLabel'>{port.port_name.replace(/,\s*$/, '')}</span>
                              <input
                                type='checkbox'
                                checked={activePorts.some((p) => p.port_id === port.port_id)}
                                onChange={() => togglePort(port)}
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {stepIndex === 2 && (
            <>
              <div className='cruiseWizard__subTabs'>
                <span className='cruiseSearchModal__tab active'>
                  Ship{shipSelection.length ? ` (${shipSelection.length})` : ''}
                </span>
                <div className='cruiseWizard__bulkActions' style={{ marginTop: 0 }}>
                  {/* Select All removed — picking nothing already searches all ships. */}
                  <span className='cruiseSearchModal__clearBtn' onClick={clearAllShips}>Clear All</span>
                </div>
              </div>

              <div className='cruiseSearchModal__fieldWrap' ref={listOpen ? listAreaRef : null}>
                <div className='cruiseSearchModal__searchInput cruiseSearchModal__chipInput mt-4' onClick={() => setListOpen(true)}>
                  {shipSelection.map((id) => {
                    const ship = ships.find((s) => s.ship_id === id)
                    if (!ship) return null
                    return (
                      <span className='cruiseSearchModal__chip' key={id}>
                        {ship.ship_name}
                        <span className='cruiseSearchModal__chipRemove' onClick={(e) => { e.stopPropagation(); toggleShip(id) }}>✕</span>
                      </span>
                    )
                  })}
                  <input
                    type='text'
                    placeholder={shipSelection.length ? '' : 'Search ships'}
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    onFocus={() => setListOpen(true)}
                  />
                </div>

                {listOpen && (
                  <div className='cruiseSearchModal__dropdown'>
                    {shipsLoading && <div className='cruiseMonthsPicker__sectionTitle mt-4'>Loading...</div>}
                    {!shipsLoading && !filteredShips.length && <div className='cruiseMonthsPicker__sectionTitle mt-4'>No results</div>}
                    {!shipsLoading && filteredShips.length > 0 && (
                      <div className='cruiseSearchModal__list'>
                        {sortedShips.map((ship) => (
                          <label key={ship.ship_id} className='cruiseSearchModal__listItem'>
                            <span className='cruiseSearchModal__listLabel'>{ship.ship_name}</span>
                            <input
                              type='checkbox'
                              checked={shipSelection.includes(ship.ship_id)}
                              onChange={() => toggleShip(ship.ship_id)}
                            />
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {stepIndex === 3 && (
            <>
              {/* "Search By Dates" (day-level calendar) removed — Months is the only
                  date-picking mode now.
              <div className='cruiseWizard__subTabs'>
                <span className={`cruiseSearchModal__tab ${dateTab === 'dates' ? 'active' : ''}`} onClick={() => setDateTab('dates')}>Search By Dates</span>
                <span className={`cruiseSearchModal__tab ${dateTab === 'months' ? 'active' : ''}`} onClick={() => setDateTab('months')}>Search By Months</span>
              </div>

              {dateTab === 'dates' && (
                <div className='cruiseDatesCalendar mt-4'>
                  <button type='button' className='cruiseSearchModal__navBtn cruiseSearchModal__navBtn--left' onClick={() => shiftPivot(-1)} aria-label='Previous month'>‹</button>
                  <div className='cruiseDatesCalendar__months'>
                    {visibleMonths.map(({ year, monthIndex }) => (
                      <div className='cruiseCalendarMonth' key={`${year}-${monthIndex}`}>
                        <div className='cruiseCalendarMonth__title'>{MONTH_NAMES[monthIndex]} {year}</div>
                        <div className='cruiseCalendarMonth__weekRow'>
                          {DAY_LABELS.map((label, index) => (
                            <span key={index}>{label}</span>
                          ))}
                        </div>
                        <div className='cruiseCalendarMonth__grid'>
                          {buildCalendarCells(year, monthIndex).map((day, index) => {
                            if (!day) return <span key={index} />
                            const disabled = isBeforeCurrentMonth(year, monthIndex)
                            const selected = selectedDay && selectedDay.year === year && selectedDay.month === monthIndex && selectedDay.day === day
                            return (
                              <button
                                type='button'
                                key={index}
                                className={`cruiseCalendarMonth__day ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                                disabled={disabled}
                                onClick={() => handleDayClick(year, monthIndex, day)}
                              >
                                {day}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  <button type='button' className='cruiseSearchModal__navBtn cruiseSearchModal__navBtn--right' onClick={() => shiftPivot(1)} aria-label='Next month'>›</button>
                </div>
              )}
              */}

              <div className='cruiseWizard__subTabs'>
                <span className='cruiseSearchModal__tab active'>Search By Months</span>
              </div>

              <div className='cruiseSearchModal__fieldWrap' ref={listOpen ? listAreaRef : null}>
                <div
                  className={`cruiseSearchModal__searchInput mt-4 cruiseSearchModal__searchInput--trigger ${month ? '' : 'cruiseSearchModal__searchInput--placeholder'}`}
                  onClick={() => setListOpen((v) => !v)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setListOpen((v) => !v) }}
                >
                  {month ? formatMonthLabel(month) : 'Select a month'}
                </div>

                {dateTab === 'months' && listOpen && (
                  <div className='cruiseMonthsPicker cruiseSearchModal__dropdown'>
                    {/* <div className='cruiseMonthsPicker__sectionTitle'>Special dates</div>
                    <div className='cruiseSearchModal__pillRow'>
                      {SPECIAL_DATES.map((label) => (
                        <span
                          key={label}
                          className={`cruiseSearchModal__pill ${specialDate === label ? 'selected' : ''}`}
                          onClick={() => setSpecialDate(specialDate === label ? null : label)}
                        >
                          {label}
                        </span>
                      ))}
                    </div> */}

                    {/* <div className='cruiseMonthsPicker__sectionTitle mt-4'>North America</div> */}
                    <div className='cruiseMonthsPicker__years'>
                      <button type='button' className='cruiseSearchModal__navBtn' onClick={() => setYearWindowStart((y) => Math.max(y - 1, currentYear))} aria-label='Previous years'>‹</button>
                      <div className='cruiseMonthsPicker__yearGrid'>
                        {visibleYears.map((year) => (
                          <div className='cruiseMonthsPicker__yearBlock' key={year}>
                            <div className='cruiseMonthsPicker__yearLabel'>{year}</div>
                            <div className='cruiseMonthsPicker__monthGrid'>
                              {MONTH_SHORT.map((label, monthIndex) => {
                                const disabled = isBeforeCurrentMonth(year, monthIndex)
                                const selected = month === monthKey(year, monthIndex)
                                return (
                                  <span
                                    key={label}
                                    className={`cruiseSearchModal__pill cruiseSearchModal__pill--month ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                                    onClick={() => handleMonthClick(year, monthIndex)}
                                  >
                                    {label}
                                  </span>
                                )
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                      <button type='button' className='cruiseSearchModal__navBtn' onClick={() => setYearWindowStart((y) => y + 1)} aria-label='Next years'>›</button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className='cruiseSearchModal__footer'>
          <button type='button' className='cruiseSearchModal__skipBtn' onClick={skipStep}>Skip</button>
          {stepIndex < STEPS.length - 1 ? (
            <button type='button' className='commonBtn cruiseSearchModal__applyBtn' onClick={goNext}>Next</button>
          ) : (
            <button type='button' className='commonBtn cruiseSearchModal__applyBtn' onClick={handleDone}>Done</button>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default CruiseSearchWizard
